import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DifferenceIcon from '@mui/icons-material/Difference';
import { DiffEditor } from '@monaco-editor/react';
import config from '../config';
import { toast } from 'react-toastify';

export default function Approvals({ user }) {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [diffOriginal, setDiffOriginal] = useState('');
  const [diffModified, setDiffModified] = useState('');
  const [diffTitle, setDiffTitle] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [pendingRes, historyRes] = await Promise.all([
        fetch(`${config.API_BASE}/approvals/pending`),
        fetch(`${config.API_BASE}/approvals/all`),
      ]);
      if (pendingRes.ok) setPending(await pendingRes.json());
      if (historyRes.ok) setHistory(await historyRes.json());
    } catch { /* backend offline */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleViewDiff = async (item) => {
    // Fetch current DB file to compare
    try {
      const res = await fetch(`${config.API_BASE}/rulefile`);
      const data = await res.json();
      const dbContent = data.success ? data.content : '<!-- No file in database -->';
      const agentContent = item.ruleData?.content || '<!-- No content -->';
      setDiffOriginal(dbContent);
      setDiffModified(agentContent);
      setDiffTitle(`Changes by ${item.submittedBy}: ${item.description}`);
      setDiffDialogOpen(true);
    } catch {
      toast.error('Cannot fetch DB file for comparison');
    }
  };

  const handleApprove = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${config.API_BASE}/approvals/approve/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user?.username, comment }),
      });
      const data = await res.json();
      if (data.success) {
        // Save the agent's file content to the master DB
        if (data.ruleData && data.ruleData.content) {
          await fetch(`${config.API_BASE}/rulefile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ content: data.ruleData.content, username: user?.username, role: 'ADMIN' }),
          });
          toast.success('Approved & saved to database!');
        } else {
          toast.success('Approved!');
        }
        setComment('');
        fetchData();
      }
    } catch { toast.error('Failed to approve'); }
    finally { setLoading(false); }
  };

  const handleReject = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${config.API_BASE}/approvals/reject/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user?.username, comment }),
      });
      const data = await res.json();
      if (data.success) { toast.success('Rejected'); setComment(''); fetchData(); }
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const isAdmin = user?.role === 'ADMIN';

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        {isAdmin ? 'Pending Approvals' : 'My Submissions'}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        {isAdmin
          ? 'Review agent submissions. Click "View Changes" to see a side-by-side diff of what changed. Approve to save to the master database.'
          : 'When you submit changes, they go to admin for approval. Once approved, your changes become the new master file.'}
      </Alert>

      {/* Pending */}
      {pending.length === 0 ? (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No pending approvals</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {pending.map((item) => (
            <Grid item xs={12} key={item.id}>
              <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip label={item.action} size="small" color="warning" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.description}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      by <strong>{item.submittedBy}</strong> • {new Date(item.submittedAt).toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Summary info */}
                  <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {item.ruleData?.ruleCount && (
                      <Chip label={`${item.ruleData.ruleCount} rules in file`} size="small" variant="outlined" />
                    )}
                    <Chip label="Full file submitted" size="small" variant="outlined" color="info" />
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="small" variant="outlined" startIcon={<DifferenceIcon />}
                      onClick={() => handleViewDiff(item)}>
                      View Changes
                    </Button>

                    {isAdmin && (
                      <>
                        <TextField size="small" placeholder="Comment (optional)" value={comment}
                          onChange={(e) => setComment(e.target.value)} sx={{ flex: 1 }} />
                        <Button variant="contained" color="success" size="small" startIcon={<CheckIcon />}
                          onClick={() => handleApprove(item.id)} disabled={loading}>
                          Approve & Save
                        </Button>
                        <Button variant="outlined" color="error" size="small" startIcon={<CloseIcon />}
                          onClick={() => handleReject(item.id)} disabled={loading}>
                          Reject
                        </Button>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* History */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" sx={{ mb: 2 }}>History</Typography>
      {history.filter(h => h.status !== 'PENDING').length === 0 ? (
        <Typography color="text.secondary">No history yet</Typography>
      ) : (
        history.filter(h => h.status !== 'PENDING').slice(0, 15).map((item) => (
          <Box key={item.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, p: 1.5, borderRadius: 1, bgcolor: '#fafafa' }}>
            <Chip label={item.status} size="small" color={item.status === 'APPROVED' ? 'success' : 'error'} />
            <Typography variant="body2" sx={{ flex: 1 }}>{item.description}</Typography>
            <Typography variant="caption" color="text.secondary">
              {item.submittedBy} → {item.reviewedBy || '?'} • {new Date(item.submittedAt).toLocaleDateString()}
            </Typography>
            {item.reviewComment && (
              <Chip label={item.reviewComment} size="small" variant="outlined" />
            )}
          </Box>
        ))
      )}

      {/* Diff Dialog */}
      <Dialog open={diffDialogOpen} onClose={() => setDiffDialogOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>{diffTitle}</DialogTitle>
        <DialogContent sx={{ p: 0, height: '70vh' }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, py: 1, bgcolor: '#f5f5f5', display: 'flex', gap: 2 }}>
              <Chip label="← Current DB File" size="small" color="error" variant="outlined" />
              <Chip label="→ Agent's Modified File" size="small" color="success" variant="outlined" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <DiffEditor
                height="100%"
                language="xml"
                original={diffOriginal}
                modified={diffModified}
                theme="vs-dark"
                options={{ readOnly: true, renderSideBySide: true, fontSize: 13 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiffDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
